import { readFileSync } from 'node:fs';
import { resolve } from 'node:path';
import { packageVersion } from '../../version.js';
import type { ParsedArgs } from '../args.js';
import { flag, numberFlag } from '../args.js';
import { CliError, ExitCode } from '../errors.js';
import { writeBytesOutput } from '../files.js';
import type { CommandResult } from '../output.js';
import { packageRoot } from '../package-files.js';

interface DocumentationSource {
  id: string;
  title: string;
  authority: string;
  kind: 'bundled' | 'remote';
  path?: string;
  url?: string;
}

export interface DocsCommandDependencies {
  fetch?: typeof fetch;
}

const DOCUMENTATION_SOURCES: readonly DocumentationSource[] = [
  {
    id: 'cli-output',
    title: 'CLI output contract',
    authority: '@zaparoo/cli package',
    kind: 'bundled',
    path: 'docs/cli-output.md',
  },
  {
    id: 'agent-evaluations',
    title: 'Agent evaluation scenarios',
    authority: '@zaparoo/cli package',
    kind: 'bundled',
    path: 'docs/skill-scenarios.md',
  },
  {
    id: 'mcp-boundary',
    title: 'MCP boundary and future trigger criteria',
    authority: '@zaparoo/cli package',
    kind: 'bundled',
    path: 'docs/mcp-boundary.md',
  },
  {
    id: 'zapscript',
    title: 'Bundled ZapScript reference',
    authority: '@zaparoo/cli package generated from current Core behavior',
    kind: 'bundled',
    path: 'skills/zaparoo-zapscript/references/zapscript.md',
  },
  {
    id: 'integration',
    title: 'Zaparoo integration source authority',
    authority: '@zaparoo/cli package',
    kind: 'bundled',
    path: 'skills/zaparoo-development/references/integration.md',
  },
  {
    id: 'online-user-api-guide',
    title: 'Online User API skill reference',
    authority: '@zaparoo/cli package derived from public OpenAPI',
    kind: 'bundled',
    path: 'skills/zaparoo-online/references/user-api.md',
  },
  {
    id: 'core-api',
    title: 'Public Core API documentation',
    authority: 'Zaparoo public documentation',
    kind: 'remote',
    url: 'https://zaparoo.org/docs/core/api/',
  },
  {
    id: 'core-methods',
    title: 'Public Core API methods',
    authority: 'Zaparoo public documentation',
    kind: 'remote',
    url: 'https://zaparoo.org/docs/core/api/methods/',
  },
  {
    id: 'core-notifications',
    title: 'Public Core API notifications',
    authority: 'Zaparoo public documentation',
    kind: 'remote',
    url: 'https://zaparoo.org/docs/core/api/notifications/',
  },
  {
    id: 'core-encryption',
    title: 'Public Core pairing and encryption',
    authority: 'Zaparoo public documentation',
    kind: 'remote',
    url: 'https://zaparoo.org/docs/core/api/encryption/',
  },
  {
    id: 'online-openapi',
    title: 'Online User API OpenAPI contract',
    authority: 'Sole Online User API authority',
    kind: 'remote',
    url: 'https://developers.zaparoo.com/openapi-user.yaml',
  },
] as const;

export async function docsCommand(
  args: ParsedArgs,
  dependencies: DocsCommandDependencies = {},
): Promise<CommandResult> {
  const action = args.positionals[1] ?? 'list';
  if (action === 'list') return listSources();
  if (action === 'search') return searchSources(args);
  if (action === 'get') return getSource(args, dependencies);
  throw new CliError(`Unknown docs action "${action}"`, ExitCode.Usage);
}

function listSources(): CommandResult {
  const sources = DOCUMENTATION_SOURCES.map(publicSource);
  return {
    data: { packageVersion, sources },
    human: sources.map((source) => `${source.id}\t${source.title}`).join('\n'),
  };
}

function searchSources(args: ParsedArgs): CommandResult {
  const query = args.positionals.slice(2).join(' ').trim();
  if (!query) throw new CliError('docs search requires <query>', ExitCode.Usage);
  const limit = numberFlag(args.flags, 'limit') ?? 10;
  if (!Number.isInteger(limit) || limit < 1 || limit > 50) {
    throw new CliError('--limit must be an integer between 1 and 50', ExitCode.Usage);
  }
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  const results: Array<Record<string, unknown>> = [];
  for (const source of DOCUMENTATION_SOURCES) {
    const metadata = `${source.id} ${source.title} ${source.authority} ${source.url ?? ''}`;
    if (terms.every((term) => metadata.toLowerCase().includes(term))) {
      results.push({ ...publicSource(source), match: 'metadata' });
    }
    if (source.kind !== 'bundled' || !source.path) continue;
    const content = readBundled(source.path);
    for (const section of sections(content)) {
      if (!terms.every((term) => section.toLowerCase().includes(term))) continue;
      results.push({
        ...publicSource(source),
        match: 'content',
        snippet: section.slice(0, 400),
      });
      if (results.length >= limit) break;
    }
    if (results.length >= limit) break;
  }
  return {
    data: {
      query,
      results: results.slice(0, limit),
      limited: results.length >= limit,
      packageVersion,
    },
    human: results
      .slice(0, limit)
      .map((result) => `${result.id}: ${result.title}`)
      .join('\n'),
  };
}

async function getSource(
  args: ParsedArgs,
  dependencies: DocsCommandDependencies,
): Promise<CommandResult> {
  const id = args.positionals[2];
  if (!id) throw new CliError('docs get requires <source-id>', ExitCode.Usage);
  const source = DOCUMENTATION_SOURCES.find((entry) => entry.id === id);
  if (!source) {
    throw new CliError(`Unknown documentation source "${id}"; run docs list`, ExitCode.Usage);
  }
  const maxSize = numberFlag(args.flags, 'max-size') ?? 524_288;
  if (!Number.isInteger(maxSize) || maxSize < 1 || maxSize > 2_097_152) {
    throw new CliError('--max-size must be an integer between 1 and 2097152', ExitCode.Usage);
  }

  const document =
    source.kind === 'bundled'
      ? bundledDocument(source, maxSize)
      : await remoteDocument(source, maxSize, args.options.timeoutSeconds, dependencies.fetch);
  const output = flag(args.flags, 'output');
  if (output) {
    writeBytesOutput(document.bytes, output);
    return {
      data: {
        source: publicSource(source),
        contentType: document.contentType,
        output,
        size: document.bytes.length,
      },
      human: `Wrote ${id} documentation to ${output}`,
    };
  }

  const text = new TextDecoder().decode(document.bytes);
  const inlineLimit = 40_000;
  return {
    data: {
      source: publicSource(source),
      contentType: document.contentType,
      content: text.slice(0, inlineLimit),
      retrievedBytes: document.bytes.length,
      truncated: text.length > inlineLimit,
      guidance: text.length > inlineLimit ? 'Use --output <path> for complete content.' : undefined,
    },
    human: text.slice(0, inlineLimit),
  };
}

function bundledDocument(
  source: DocumentationSource,
  maxSize: number,
): { bytes: Uint8Array; contentType: string } {
  if (!source.path) throw new CliError('Bundled documentation path is missing', ExitCode.General);
  const bytes = readFileSync(resolve(packageRoot(), source.path));
  if (bytes.length > maxSize) {
    throw new CliError(`${source.id} exceeds --max-size (${bytes.length} bytes)`, ExitCode.Usage);
  }
  return { bytes, contentType: 'text/markdown; charset=utf-8' };
}

async function remoteDocument(
  source: DocumentationSource,
  maxSize: number,
  timeoutSeconds: number,
  fetchImpl: typeof fetch = globalThis.fetch,
): Promise<{ bytes: Uint8Array; contentType: string }> {
  if (!source.url) throw new CliError('Remote documentation URL is missing', ExitCode.General);
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutSeconds * 1000);
  try {
    const response = await fetchImpl(source.url, {
      method: 'GET',
      headers: { Accept: 'text/markdown, text/yaml, text/plain, text/html' },
      redirect: 'error',
      signal: controller.signal,
    });
    if (!response.ok) {
      throw new CliError(
        `Documentation request failed with status ${response.status}`,
        ExitCode.Connection,
      );
    }
    const contentLength = Number(response.headers.get('content-length'));
    if (Number.isFinite(contentLength) && contentLength > maxSize) {
      throw new CliError(`${source.id} exceeds --max-size`, ExitCode.Usage);
    }
    const bytes = await readBoundedBody(response, maxSize, source.id);
    return {
      bytes,
      contentType: response.headers.get('content-type') ?? 'text/plain; charset=utf-8',
    };
  } catch (error) {
    if (error instanceof CliError) throw error;
    if (controller.signal.aborted) {
      throw new CliError('Documentation request timed out', ExitCode.Timeout);
    }
    throw new CliError(
      `Documentation request failed: ${error instanceof Error ? error.message : String(error)}`,
      ExitCode.Connection,
    );
  } finally {
    clearTimeout(timer);
  }
}

async function readBoundedBody(
  response: Response,
  maxSize: number,
  sourceId: string,
): Promise<Uint8Array> {
  if (!response.body) return new Uint8Array();
  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let size = 0;
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      size += value.length;
      if (size > maxSize) {
        await reader.cancel();
        throw new CliError(`${sourceId} exceeds --max-size`, ExitCode.Usage);
      }
      chunks.push(value);
    }
  } finally {
    reader.releaseLock();
  }
  const bytes = new Uint8Array(size);
  let offset = 0;
  for (const chunk of chunks) {
    bytes.set(chunk, offset);
    offset += chunk.length;
  }
  return bytes;
}

function readBundled(path: string): string {
  return readFileSync(resolve(packageRoot(), path), 'utf8');
}

function sections(content: string): string[] {
  return content
    .split(/\n\s*\n/)
    .map((section) => section.replace(/\s+/g, ' ').trim())
    .filter(Boolean);
}

function publicSource(source: DocumentationSource): Record<string, unknown> {
  return {
    id: source.id,
    title: source.title,
    authority: source.authority,
    kind: source.kind,
    url: source.url,
    packageVersion: source.kind === 'bundled' ? packageVersion : undefined,
  };
}
